import React, { useState } from 'react';

import { Collapse, Divider, IconButton, Typography } from '@mui/material';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { makeStyles } from 'tss-react/mui';

import { isUserDecisionStatus } from './redline-state-helpers';
import type { SuggestionStatus } from '../../../../chat-types';
import { SuggestionStatusChip } from '../CodeSuggestionListElement/SuggestionItemHeader';

interface StyleProps {
  isSelected: boolean;
}

const useStyles = makeStyles<StyleProps>()((theme, { isSelected }) => ({
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
  },
  card: {
    '&:hover': {
      border: `1px solid ${theme.appColors.primary}`,
    },
    backgroundColor: '#FAFAFC',
    border: isSelected
      ? `1px solid ${theme.appColors.primary}`
      : `1px solid ${theme.appColors.rule}`,
    borderRadius: 8,
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
  },
  expandIcon: {
    height: 16,
    width: 16,
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1),
  },
  headerRight: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    gap: theme.spacing(0.25),
    marginLeft: 'auto',
  },
  title: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

interface ExpandableRedlineCardProps {
  /**
   * The expandable body content, revealed when the card is expanded.
   */
  children: React.ReactNode;
  /**
   * DOM id used to scroll/anchor the card from the editor's suggestion alerts.
   */
  elementId: string;
  /**
   * Whether the card is the currently selected suggestion (highlights the border).
   */
  isSelected: boolean;
  /**
   * Invoked when the card body (not the expand toggle) is clicked — used to
   * navigate to the suggestion in the document.
   */
  onCardClick: () => void;
  status: SuggestionStatus;
  /**
   * The single-line summary shown in the collapsed header (typically the reasoning).
   */
  title: string;
}

/**
 * The shared chrome for a redline chat card: a clickable, selectable card with a
 * header (title + optional status chip + expand toggle) and a collapsible body.
 * Used by both edit and insert rows so they look and behave identically.
 */
export const ExpandableRedlineCard = React.memo(function ExpandableRedlineCard({
  children,
  elementId,
  isSelected,
  onCardClick,
  status,
  title,
}: ExpandableRedlineCardProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false);
  const { classes } = useStyles({ isSelected });

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={classes.card} id={elementId} onClick={onCardClick}>
      <div className={classes.header}>
        <Typography className={classes.title} variant="subtitle2">
          {title}
        </Typography>
        <div className={classes.headerRight}>
          {isUserDecisionStatus(status) && <SuggestionStatusChip status={status} />}
          <IconButton size="small" onClick={handleToggleExpand}>
            {isExpanded ? (
              <ChevronUpIcon className={classes.expandIcon} />
            ) : (
              <ChevronDownIcon className={classes.expandIcon} />
            )}
          </IconButton>
        </div>
      </div>
      <Collapse in={isExpanded}>
        <Divider />
        <div className={classes.body}>{children}</div>
      </Collapse>
    </div>
  );
});

const usePreviewSectionStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  title: {
    fontStyle: 'italic',
    textDecoration: 'underline',
  },
}));

/**
 * A labeled section inside a redline card body: an italic underlined title
 * (e.g. "Removed text:", "Change:") above the preview content. Shared by the
 * insert, remove, and edit (visual diff) cards.
 */
export const RedlinePreviewSection = React.memo(function RedlinePreviewSection({
  children,
  title,
}: {
  children: React.ReactNode;
  title: string;
}): JSX.Element {
  const { classes } = usePreviewSectionStyles();

  return (
    <div className={classes.container}>
      <Typography className={classes.title} variant="body2">
        {title}
      </Typography>
      {children}
    </div>
  );
});

/**
 * Displays the full reasoning text for a redline card body. Shared by edit and
 * insert cards.
 */
export const RedlineReasoning = React.memo(function RedlineReasoning({
  reasoning,
}: {
  reasoning: string;
}): JSX.Element {
  return (
    <div>
      <Typography sx={{ fontStyle: 'italic', textDecoration: 'underline' }} variant="body2">
        Explanation:
      </Typography>
      <Typography sx={{ color: 'text.secondary' }} variant="body2">
        {reasoning}
      </Typography>
    </div>
  );
});
